import { useState, useEffect, useRef } from 'react'
import {
  getProfissionais,
  getUnidades,
  createProfissional,
  updateProfissional,
  deleteProfissional,
  uploadImage,
  type Profissional,
  type Unidade,
} from '@/api/adminApi'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import adminStyles from './Admin.module.css'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './AdminProfissionaisPage.module.css'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i

const FORM_EMPTY = {
  unidade_id: null as number | null,
  nome: '',
  especialidade: '',
  email: '',
  senha: '',
  foto_url: null as string | null,
  ativo: true,
  ordem: 0,
}

function initials(nome: string): string {
  const parts = nome.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function AdminProfissionaisPage() {
  const fileRef = useRef<HTMLInputElement>(null)
  const [list, setList] = useState<Profissional[]>([])
  const [unidades, setUnidades] = useState<Unidade[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [editing, setEditing] = useState<Profissional | null>(null)
  const [form, setForm] = useState(FORM_EMPTY)
  const [fotoPreview, setFotoPreview] = useState<string | null>(null)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formErrors, setFormErrors] = useState<{ nome?: string; email?: string; senha?: string }>({})
  const { notifySaved, notifyDeleted, notifyError, notifyValidation } = useSaveFeedback()

  useEffect(() => {
    getUnidades(true).then(setUnidades).catch(() => {})
  }, [])

  const load = () => {
    setError(null)
    getProfissionais(true)
      .then(setList)
      .catch((e) => setError(e instanceof Error ? e.message : 'Erro ao carregar'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const resetForm = () => {
    setEditing(null)
    setForm(FORM_EMPTY)
    setFotoPreview(null)
    setFormErrors({})
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errors: { nome?: string; email?: string; senha?: string } = {}
    if (!form.nome.trim()) errors.nome = 'Nome é obrigatório.'
    if (!form.email.trim()) errors.email = 'E-mail é obrigatório.'
    if (form.email && !EMAIL_REGEX.test(form.email.trim())) errors.email = 'E-mail inválido.'
    if (!editing && !form.senha.trim()) errors.senha = 'Senha é obrigatória no cadastro.'
    setFormErrors(errors)
    if (Object.keys(errors).length > 0) {
      const first = errors.nome ?? errors.email ?? errors.senha
      if (first) notifyValidation(first)
      return
    }

    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      if (editing) {
        await updateProfissional(editing.id, {
          unidade_id: form.unidade_id,
          nome: form.nome.trim(),
          especialidade: form.especialidade.trim() || undefined,
          foto_url: form.foto_url,
          email: form.email.trim(),
          senha: form.senha.trim() || undefined,
          ativo: form.ativo,
          ordem: form.ordem,
        })
        setSuccess('Profissional actualizado.')
        notifySaved('Profissional actualizado.')
      } else {
        await createProfissional({
          unidade_id: form.unidade_id,
          nome: form.nome.trim(),
          especialidade: form.especialidade.trim() || undefined,
          foto_url: form.foto_url,
          email: form.email.trim(),
          senha: form.senha.trim(),
          ativo: form.ativo,
          ordem: form.ordem,
        })
        setSuccess('Profissional adicionado.')
        notifySaved('Profissional adicionado.')
      }
      resetForm()
      load()
    } catch (err) {
      notifyError(err, 'Erro ao guardar')
      setError(err instanceof Error ? err.message : 'Erro ao guardar')
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (p: Profissional) => {
    setEditing(p)
    setForm({
      unidade_id: p.unidade_id ?? null,
      nome: p.nome,
      especialidade: p.especialidade ?? '',
      email: p.email ?? '',
      senha: '',
      foto_url: p.foto_url ?? null,
      ativo: !!p.ativo,
      ordem: p.ordem ?? 0,
    })
    setFotoPreview(p.foto_url ? resolveMediaUrl(p.foto_url) : null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Excluir este profissional?')) return
    setError(null)
    try {
      await deleteProfissional(id)
      if (editing?.id === id) resetForm()
      notifyDeleted('Profissional excluído.')
      load()
    } catch (err) {
      notifyError(err, 'Erro ao excluir')
      setError(err instanceof Error ? err.message : 'Erro ao excluir')
    }
  }

  const handlePhotoPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingPhoto(true)
    setError(null)
    try {
      const { url, preview } = await uploadImage(file)
      setForm((f) => ({ ...f, foto_url: url }))
      setFotoPreview(preview)
      notifySaved('Foto carregada.')
    } catch (err) {
      notifyError(err, 'Erro no upload da foto')
      setError(err instanceof Error ? err.message : 'Erro no upload da foto')
    } finally {
      setUploadingPhoto(false)
      e.target.value = ''
    }
  }

  const clearPhoto = () => {
    setForm((f) => ({ ...f, foto_url: null }))
    setFotoPreview(null)
  }

  const unidadeNome = (id: number | null) => {
    if (id == null) return 'Todas as unidades'
    return unidades.find((u) => u.id === id)?.nome ?? '—'
  }

  const previewSrc = fotoPreview || (form.foto_url ? resolveMediaUrl(form.foto_url) : null)

  if (loading) return <p className={adminStyles.placeholderMsg}>A carregar…</p>

  return (
    <>
      <header className={adminStyles.sectionHeader}>
        <h1 className={adminStyles.sectionTitle}>Profissionais</h1>
        <p className={adminStyles.sectionSubtitle}>
          Cadastre a equipa que aparece no app. Adicione foto, especialidade e associe a uma unidade.
        </p>
      </header>

      {error && (
        <div className={adminStyles.erroSection}>
          <p className={adminStyles.erro}>{error}</p>
        </div>
      )}
      {success && (
        <div className={adminStyles.successSection}>
          <p className={adminStyles.successMsg}>{success}</p>
        </div>
      )}

      <div className={adminStyles.sectionCard}>
        <form className={styles.proForm} onSubmit={handleSubmit}>
          <h2 className={styles.formTitle}>
            {editing ? `Editar — ${editing.nome}` : 'Novo profissional'}
          </h2>

          <div className={styles.formLayout}>
            <div className={styles.photoBlock}>
              {previewSrc ? (
                <img src={previewSrc} alt="" className={styles.avatar} />
              ) : (
                <div className={styles.avatarPlaceholder} aria-hidden>
                  {form.nome.trim() ? initials(form.nome) : '👤'}
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className={styles.hiddenInput}
                onChange={handlePhotoPick}
              />
              <div className={styles.photoActions}>
                <button
                  type="button"
                  className={styles.photoBtn}
                  disabled={uploadingPhoto}
                  onClick={() => fileRef.current?.click()}
                >
                  {uploadingPhoto ? 'A enviar…' : previewSrc ? 'Alterar foto' : 'Carregar foto'}
                </button>
                {previewSrc && (
                  <button type="button" className={styles.photoBtnDanger} onClick={clearPhoto}>
                    Remover foto
                  </button>
                )}
              </div>
              <p className={styles.photoHint}>JPG, PNG ou WebP. Recomendado: rosto centrado, quadrado.</p>
            </div>

            <div className={styles.fieldsGrid}>
              <div className={`${adminStyles.formGroup} ${styles.field} ${styles.span2}`}>
                <label htmlFor="prof-nome">Nome *</label>
                <input
                  id="prof-nome"
                  value={form.nome}
                  onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                  placeholder="Nome do profissional"
                  required
                />
                {formErrors.nome && <span className={adminStyles.fieldError}>{formErrors.nome}</span>}
              </div>
              <div className={`${adminStyles.formGroup} ${styles.field}`}>
                <label htmlFor="prof-unidade">Unidade</label>
                <select
                  id="prof-unidade"
                  value={form.unidade_id ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, unidade_id: e.target.value ? Number(e.target.value) : null }))
                  }
                >
                  <option value="">Todas</option>
                  {unidades.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className={`${adminStyles.formGroup} ${styles.field}`}>
                <label htmlFor="prof-esp">Especialidade</label>
                <input
                  id="prof-esp"
                  value={form.especialidade}
                  onChange={(e) => setForm((f) => ({ ...f, especialidade: e.target.value }))}
                  placeholder="Ex.: Cabelo, Barba"
                />
              </div>
              <div className={`${adminStyles.formGroup} ${styles.field} ${styles.span2}`}>
                <label htmlFor="prof-email">E-mail *</label>
                <input
                  id="prof-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="exemplo@email.com"
                />
                {formErrors.email && <span className={adminStyles.fieldError}>{formErrors.email}</span>}
              </div>
              <div className={`${adminStyles.formGroup} ${styles.field} ${styles.span2}`}>
                <label htmlFor="prof-senha">
                  {editing ? 'Nova senha (opcional)' : 'Senha de acesso *'}
                </label>
                <input
                  id="prof-senha"
                  type="password"
                  value={form.senha}
                  onChange={(e) => setForm((f) => ({ ...f, senha: e.target.value }))}
                  placeholder={editing ? 'Deixe vazio para manter a actual' : 'Senha do portal profissional'}
                />
                {formErrors.senha && <span className={adminStyles.fieldError}>{formErrors.senha}</span>}
              </div>
              <div className={`${styles.metaRow} ${styles.span2}`}>
                <div className={`${adminStyles.formGroup} ${styles.field}`}>
                  <label htmlFor="prof-ordem">Ordem</label>
                  <input
                    id="prof-ordem"
                    type="number"
                    min={0}
                    value={form.ordem}
                    onChange={(e) => setForm((f) => ({ ...f, ordem: Number(e.target.value) || 0 }))}
                  />
                </div>
                <label className={styles.ativoCheck} htmlFor="prof-ativo">
                  <input
                    id="prof-ativo"
                    type="checkbox"
                    checked={form.ativo}
                    onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))}
                  />
                  <span>Activo no app</span>
                </label>
              </div>
              <div className={`${styles.formActions} ${styles.span2}`}>
                <button type="submit" className={adminStyles.btnSalvar} disabled={saving || uploadingPhoto}>
                  {saving ? 'A guardar…' : editing ? 'Guardar alterações' : 'Adicionar profissional'}
                </button>
                {editing && (
                  <button type="button" className={adminStyles.btnCancel} onClick={resetForm}>
                    Cancelar edição
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      <div className={adminStyles.sectionListCard}>
        <h2 className={adminStyles.sectionListTitle}>
          Equipa ({list.length})
        </h2>
        {list.length === 0 ? (
          <p className={adminStyles.listEmpty}>Nenhum profissional. Adicione o primeiro acima.</p>
        ) : (
          <ul className={styles.cardGrid}>
            {list.map((p) => {
              const isEditing = editing?.id === p.id
              const foto = p.foto_url ? resolveMediaUrl(p.foto_url) : null
              return (
                <li
                  key={p.id}
                  className={`${styles.proCard} ${!p.ativo ? styles.proCardInactive : ''} ${isEditing ? styles.proCardEditing : ''}`}
                >
                  <div className={styles.proCardTop}>
                    {foto ? (
                      <img src={foto} alt="" className={styles.proCardAvatar} />
                    ) : (
                      <div className={styles.proCardAvatarPh}>{initials(p.nome)}</div>
                    )}
                    <div className={styles.proCardBody}>
                      <p className={styles.proCardName}>{p.nome}</p>
                      {p.especialidade && <p className={styles.proCardSpec}>{p.especialidade}</p>}
                      <p className={styles.proCardMeta}>{unidadeNome(p.unidade_id)}</p>
                      {p.email && <p className={styles.proCardMeta}>{p.email}</p>}
                    </div>
                  </div>
                  <div className={styles.proCardTags}>
                    <span className={styles.tag}>Ordem {p.ordem}</span>
                    {!p.ativo && <span className={`${styles.tag} ${styles.tagDanger}`}>Inactivo</span>}
                  </div>
                  <div className={styles.proCardActions}>
                    <button
                      type="button"
                      className={adminStyles.btnSecondary}
                      onClick={() => handleEdit(p)}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      className={adminStyles.btnDanger}
                      onClick={() => handleDelete(p.id)}
                    >
                      Excluir
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </>
  )
}

